import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { fromLonLat } from 'ol/proj';
import { Fill, Stroke, Style } from 'ol/style';
import Text from 'ol/style/Text';

interface TransactionsFaoPrixM2LayerOptions {
  visible: boolean;
  labelMinZoom?: number;
}

// Must match createTransactionsFaoLayer dot dimensions
const DOT_RADIUS = 5;
const DOT_STROKE_WIDTH = 2;
const DOT_OUTER_RADIUS = DOT_RADIUS + DOT_STROKE_WIDTH / 2;
const LABEL_GAP = 3;

const isSvbVerifiee = (transaction: any): boolean =>
  transaction.status === 'SVB_Verifiee' || !!transaction.SVB_Verifiee;

const formatPrixM2Label = (value: number): string => {
  const formatted = Math.round(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return `${formatted}/m2`;
};

const selectLatestVerifiedByLocation = (parcelles: any[]): any[] => {
  const byLocation = new Map<string, any>();

  parcelles.forEach((transaction) => {
    if (!isSvbVerifiee(transaction)) return;
    if (transaction.Prix_m2_habitable == null) return;
    if (!transaction.position_sur_map?.coordinates) return;
    if (transaction.PrixNonRepresentatif) return;
    if (transaction.qpt) return;

    const key = transaction.position_sur_map.coordinates.join(',');
    const existing = byLocation.get(key);
    if (!existing || transaction.Date_Transaction > existing.Date_Transaction) {
      byLocation.set(key, transaction);
    }
  });

  return Array.from(byLocation.values());
};

export function createTransactionsFaoPrixM2Layer(options: TransactionsFaoPrixM2LayerOptions) {
  const { visible, labelMinZoom = 16 } = options;
  const source = new VectorSource({ features: [] as any[] });

  const layer = new VectorLayer({
    source,
    visible,
    properties: { name: 'transactions_fao_prix_m2' },
    style: (feature, resolution) => {
      const maxResolution = 156543.03392804097 / Math.pow(2, labelMinZoom);
      if (resolution >= maxResolution) {
        return undefined;
      }

      const label = feature.get('label');
      if (!label) return undefined;

      return new Style({
        text: new Text({
          text: label,
          font: 'bold 11px sans-serif',
          fill: new Fill({ color: '#333333' }),
          stroke: new Stroke({ color: '#ffffff', width: 3 }),
          backgroundFill: new Fill({ color: 'rgba(255, 255, 255, 0.92)' }),
          backgroundStroke: new Stroke({ color: 'rgba(0, 0, 0, 0.15)', width: 1 }),
          padding: [3, 5, 3, 5],
          textAlign: 'left',
          textBaseline: 'bottom',
          offsetX: DOT_OUTER_RADIUS + LABEL_GAP,
          offsetY: -(DOT_OUTER_RADIUS + LABEL_GAP),
          overflow: true,
        }),
      });
    },
    zIndex: 11,
  });

  const updateFromParcelles = (parcellesData: any[]) => {
    if (!layer.getVisible()) return;

    source.clear();

    selectLatestVerifiedByLocation(parcellesData).forEach((transaction) => {
      const [lon, lat] = transaction.position_sur_map.coordinates;
      const feature = new Feature({
        geometry: new Point(fromLonLat([lon, lat])),
        label: formatPrixM2Label(transaction.Prix_m2_habitable),
        name: transaction.NoParcelle,
      });
      source.addFeature(feature as any);
    });
  };

  (layer as any)._updateFromParcelles = updateFromParcelles;

  return layer;
}
